import Box from "@mui/material/Box";
import { Text } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import { Email, FTLShipment, NormalizedFtlShipmentDraft, QuotedRate } from "@src/types/api";
import { ITextFieldStyles } from "@fluentui/react/lib/TextField";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { lookupStateFromZip } from "@taskpane/utils/formUtils";
import { usePricingSubmission } from "@taskpane/hooks/usePricingSubmission";
import { useProviderConfig } from "@taskpane/hooks/useProviderConfig";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import ConfirmDialog from "@taskpane/components/ConfirmDialog";
import FeedbackDialog from "@taskpane/components/FeedbackDialog";
import { getLatestFeedbackValue } from "@taskpane/utils/feedbackUtils";
import { formatTmsOffersLoadingLabel } from "@taskpane/utils/tmsOffersLoadingLabel";
import RatingEnginesSection from "./rating-engines/RatingEnginesSection";
import { getTmsBranding } from "./providerBranding";
import { type CustomerOfferPatch } from "./CustomerOffer";
import { FtlErrorMessageBar } from "./FtlErrorMessageBar";
import { FtlPanelHeader } from "./FtlPanelHeader";
import { FtlShipmentDetailsGrid } from "./FtlShipmentDetailsGrid";
import { FtlPricingActions } from "./FtlPricingActions";
import { FtlShipmentDetailsHeading } from "./FtlShipmentDetailsHeading";
import { FtlTmsOffersBlock } from "./FtlTmsOffersBlock";
import { emailShipmentsFtl, emailWithShipmentPatched } from "@taskpane/utils/emailShipments";
import { statusHistoryIdentityPayload } from "@src/load-agent/office/resolveActorEmailForThread";
import { BolDetailsForm, type BolDetailsFieldName } from "@taskpane/components/ltl/BolDetailsForm";
import {
  DEFAULT_FTL_FORM_DATA,
  FTL_ORIGINAL_STORAGE_KEY_PREFIX,
  type FtlPanelFormData,
  pickBolFormSlice,
  serializeFtlFormData,
  UNEXPECTED_ERROR_MESSAGE,
} from "./ftlFormModel";
import { styles } from "./FTL.styles";
import {
  selectTmsRowOffers,
  stripTmsCustomerFieldsForFreshQuote,
} from "@taskpane/utils/ftlTmsRowOffers";
import { inferFieldsForFtlLoad } from "@taskpane/utils/computeInferredFormFields";
import {
  stripBolDetailErrors,
  validateBolDetailsForCapabilities,
  type BolDetailsValidationForm,
} from "@taskpane/utils/validateLTL";

interface Props {
  setActiveComponent: (name: string) => void;
  emailData: Email | null;
  setEmailData: (emailData: Email | null) => void;
  clickedFTLShipment: FTLShipment | null;
  setClickedFTLShipment: (shipment: FTLShipment | null) => void;
  selectedMode: string | null;
  setSelectedMode: (mode: "LTL" | "FTL" | null) => void;
}

export default function FTL({
  setActiveComponent,
  emailData,
  setEmailData,
  clickedFTLShipment,
  setClickedFTLShipment,
  selectedMode,
  setSelectedMode,
}: Props) {
  const providerConfig = useProviderConfig(emailData);
  const offersLoadingLabel = useMemo(
    () => formatTmsOffersLoadingLabel(providerConfig.displayName, emailData),
    [providerConfig.displayName, emailData]
  );
  const { loadApp } = useTaskpaneServices();
  const [formData, setFormData] = useState(DEFAULT_FTL_FORM_DATA);
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const [offerData, setOfferData] = useState<QuotedRate[] | null>(null);
  const [offerMargins, setOfferMargins] = useState<Record<string, number>>({});
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const errorRef = React.useRef<HTMLDivElement | null>(null);
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const lastScrolledFtlShipmentIdRef = React.useRef<string | null>(null);
  /** Only reset BOL vs shipment tab (and clear errors) when switching to a different shipment — not after persist refreshes the same row. */
  const lastHydratedFtlShipmentIdRef = React.useRef<string | null>(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [customerOfferUiKey, setCustomerOfferUiKey] = useState(0);
  const [detailsSectionTab, setDetailsSectionTab] = useState<"shipment" | "bol">("shipment");
  const [inferredFormFields, setInferredFormFields] = useState<Record<string, boolean>>({});
  const lastSavedFormRef = React.useRef<string>("");
  const formDataRef = React.useRef(DEFAULT_FTL_FORM_DATA);
  const minWeight = providerConfig.ftlMinWeight;
  const {
    submitPricing: submitPricingHook,
    loading,
    overallError,
    setOverallError,
    loaderRef,
  } = usePricingSubmission<FTLShipment>({
    mode: "FTL",
    formData,
    emailData,
    clickedShipment: clickedFTLShipment,
    selectedMode,
    requiredFields: ["originZip", "destinationZip", "pickupDate", "equipment"],
    onSuccess: (shipment: FTLShipment, offers: unknown) => {
      const list: QuotedRate[] | null = Array.isArray(offers)
        ? (offers as QuotedRate[])
        : offers && typeof offers === "object"
          ? [offers as QuotedRate]
          : null;
      const forUi = list ? stripTmsCustomerFieldsForFreshQuote(list) : null;
      const shipmentForUi =
        forUi != null ? ({ ...shipment, quotedRates: forUi } as FTLShipment) : shipment;

      if (emailData) {
        setEmailData(emailWithShipmentPatched(emailData, shipmentForUi));
      }
      setCustomerOfferUiKey((k) => k + 1);
      setOfferData(forUi);
      setOfferMargins(
        Object.fromEntries((forUi ?? []).map((offer) => [offer.id, offer.marginPct ?? 0]))
      );
      lastHydratedFtlShipmentIdRef.current = null;
      setClickedFTLShipment(shipmentForUi);
    },
  });
  const weightWarning =
    minWeight && Number(formData.weight) < minWeight
      ? `Weight is below ${minWeight.toLocaleString()} lbs — rates may not be available`
      : undefined;
  const isFormValid =
    !!formData.originZip &&
    !!formData.destinationZip &&
    !!formData.pickupDate &&
    !!formData.equipment;
  const tmsSectionTitle = "TMS Quote";
  const tmsBranding = getTmsBranding(
    emailData?.providerSetup?.tms ?? null,
    providerConfig.displayName
  );
  const tmsRowOffers = useMemo(() => selectTmsRowOffers(offerData ?? []), [offerData]);
  const isSingleTmsOffer = tmsRowOffers.length === 1;

  useEffect(() => {
    if (tmsRowOffers.length === 1) {
      setSelectedOffer(tmsRowOffers[0].id);
    } else if (tmsRowOffers.length === 0) {
      setSelectedOffer(null);
    }
  }, [tmsRowOffers]);
  const primaryTmsOffer = useMemo(() => {
    if (!tmsRowOffers.length) return null;
    if (selectedOffer) {
      const sel = tmsRowOffers.find((o) => o.id === selectedOffer);

      if (sel) return sel;
    }
    return tmsRowOffers[0] ?? null;
  }, [tmsRowOffers, selectedOffer]);
  const textFieldStyles = (hasError: boolean, hasWarning = false): Partial<ITextFieldStyles> => {
    const accentColor = hasError ? "#d13438" : hasWarning ? "#e5a100" : undefined;

    return {
      fieldGroup: {
        borderRadius: "5px",
        borderColor: accentColor ?? "#eff1f3",
        borderWidth: "2px",
        selectors: {
          "&:hover": {
            borderColor: accentColor ?? "#c7c7c7",
          },
          "&:focus, &.is-active": {
            borderColor: accentColor ?? "#0078d4",
          },
          "&& .ms-Label": {
            fontSize: "12px",
            fontWeight: 500,
            color: "#605e5c",
            marginBottom: "4px",
          },
        },
      },
      field: {
        fontSize: "13px",
        lineHeight: "18px",
        fontWeight: 400,
        color: "#323130",
      },
    };
  };
  const getTextFieldStyles = (fieldName: string) => {
    const hasError = !!errors[fieldName];
    const hasWarning = fieldName === "weight" && !!weightWarning && !hasError;

    return textFieldStyles(hasError, hasWarning);
  };

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [overallError]);
  useEffect(() => {
    const shipmentId = clickedFTLShipment?.shipmentId?.trim() ?? "";

    if (!clickedFTLShipment) {
      lastScrolledFtlShipmentIdRef.current = null;
      lastHydratedFtlShipmentIdRef.current = null;
      return;
    }
    const shouldScrollToHeader =
      shipmentId !== "" && shipmentId !== lastScrolledFtlShipmentIdRef.current;

    if (shouldScrollToHeader && headerRef.current) {
      lastScrolledFtlShipmentIdRef.current = shipmentId;
      const yOffset = -20;
      const y = headerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
    try {
      const nd = clickedFTLShipment.normalizedDraft;

      if (!nd)
        throw new Error(
          "FTL shipment missing normalizedDraft — backend must normalize before sending."
        );
      const form = nd.form;
      const rf = clickedFTLShipment.requiredFields;

      if (rf && clickedFTLShipment.shipmentId) {
        try {
          window.localStorage.setItem(
            `${FTL_ORIGINAL_STORAGE_KEY_PREFIX}${clickedFTLShipment.shipmentId}`,
            JSON.stringify(rf)
          );
        } catch {
          // ignore local storage write failures
        }
      }
      const openedNewShipment =
        shipmentId !== "" && shipmentId !== lastHydratedFtlShipmentIdRef.current;

      if (openedNewShipment) {
        lastHydratedFtlShipmentIdRef.current = shipmentId;
        const equipmentCheck = (providerConfig.ftlEquipmentOptions ?? []).find(
          (option: string) => option.toUpperCase() === (form.equipment || "").toUpperCase()
        );
        const equipment = equipmentCheck ?? (form.equipment?.trim() ? form.equipment : "VAN");
        const nextFormData = { ...DEFAULT_FTL_FORM_DATA, ...form, equipment };

        formDataRef.current = nextFormData;
        setFormData(nextFormData);
        setDetailsSectionTab("shipment");
        setErrors({});
        setInferredFormFields(inferFieldsForFtlLoad(nd as NormalizedFtlShipmentDraft));
        lastSavedFormRef.current = serializeFtlFormData(nextFormData);
      }
      const list = clickedFTLShipment.quotedRates as QuotedRate[];

      setOfferData(list.length > 0 ? list : null);
      setOfferMargins(Object.fromEntries(list.map((offer) => [offer.id, offer.marginPct ?? 0])));
      const selectedId = clickedFTLShipment.selectedQuoteId ?? null;
      const selected = selectedId ? list.find((o) => o.id === selectedId) : undefined;
      const soleId = list.length === 1 ? list[0]?.id : undefined;

      setSelectedOffer(selected?.id ?? soleId ?? null);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Could not load FTL form from this shipment (missing normalizedDraft or bad data).";

      setOverallError(msg);
    }
  }, [
    clickedFTLShipment?.shipmentId,
    clickedFTLShipment?.quotedRates,
    clickedFTLShipment?.requiredFields,
    clickedFTLShipment?.normalizedDraft,
    providerConfig.ftlEquipmentOptions,
    setOverallError,
  ]);
  const handleInputChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    try {
      const { name } = event.currentTarget;
      const el = event.currentTarget;
      const value = newValue !== undefined && newValue !== null ? newValue : (el.value ?? "");

      setFormData((prevData) => {
        const updatedData = { ...prevData, [name]: value };

        if (name === "originZip") updatedData.originState = lookupStateFromZip(value);
        else if (name === "destinationZip")
          updatedData.destinationState = lookupStateFromZip(value);
        else if (name === "weight") updatedData.weight = Number(value) || 0;
        formDataRef.current = updatedData;
        return updatedData;
      });
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };

          delete newErrors[name];
          return newErrors;
        });
      }
    } catch {
      setOverallError(UNEXPECTED_ERROR_MESSAGE);
    }
  };
  const handleBolFieldChange = useCallback((name: BolDetailsFieldName, value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      formDataRef.current = updated;
      return updated;
    });
    setInferredFormFields((inf) => {
      if (!inf[name as string]) return inf;
      const next = { ...inf };

      delete next[name as string];
      return next;
    });
    setErrors((prev) => {
      if (!prev[name as string]) return prev;
      const next = { ...prev };

      delete next[name as string];
      return next;
    });
  }, []);
  const applyFtlBolValidation = useCallback(() => {
    const caps = providerConfig.ftlBolFormCapabilities;

    if (!caps) return;
    const bolErrors = validateBolDetailsForCapabilities(
      pickBolFormSlice(formDataRef.current) as BolDetailsValidationForm,
      caps
    );

    setErrors((prev) => ({ ...stripBolDetailErrors(prev), ...bolErrors }));
  }, [providerConfig.ftlBolFormCapabilities]);
  const persistShipmentDetails = React.useCallback(
    async (formDataOverride?: FtlPanelFormData) => {
      const shipmentId = clickedFTLShipment?.shipmentId;
      const internetMessageId = emailData?.internetMessageId;

      if (!shipmentId || !internetMessageId) return;
      const nextFormData = formDataOverride ?? formDataRef.current;
      const snapshot = serializeFtlFormData(nextFormData);

      if (snapshot === lastSavedFormRef.current) return;
      const originZip = String(nextFormData.originZip ?? "").trim();
      const destinationZip = String(nextFormData.destinationZip ?? "").trim();

      if (!originZip || !destinationZip) {
        setOverallError("Origin ZIP and Destination ZIP are required before saving FTL pricing.");
        return;
      }
      try {
        await loadApp.saveFTLPricing({
          shipmentId,
          selectedMode,
          internetMessageId,
          formData: nextFormData,
          existingShipment: {
            ...clickedFTLShipment,
            quotedRates: (offerData ??
              clickedFTLShipment.quotedRates ??
              []) as FTLShipment["quotedRates"],
          },
        });
        lastSavedFormRef.current = snapshot;
        const refreshedEmailData = await loadApp.getEmails(internetMessageId);

        if (refreshedEmailData) {
          setEmailData(refreshedEmailData);
          const updatedShipment = emailShipmentsFtl(refreshedEmailData).find(
            (s) => s.shipmentId === shipmentId
          );

          if (updatedShipment) {
            setClickedFTLShipment(updatedShipment);
            setInferredFormFields(
              inferFieldsForFtlLoad(updatedShipment.normalizedDraft as NormalizedFtlShipmentDraft)
            );
            const qr = updatedShipment.quotedRates;

            if (Array.isArray(qr) && qr.length > 0) {
              setOfferData(qr as QuotedRate[]);
            }
          }
        }
      } catch (err) {
        setOverallError(err instanceof Error ? err.message : UNEXPECTED_ERROR_MESSAGE);
      }
    },
    [
      clickedFTLShipment,
      emailData?.internetMessageId,
      loadApp,
      offerData,
      selectedMode,
      setClickedFTLShipment,
      setEmailData,
      setOfferData,
      setOverallError,
    ]
  );
  const saveFtlPricingWithRates = useCallback(
    async (quotedRates: QuotedRate[]) => {
      const shipmentId = clickedFTLShipment?.shipmentId;
      const internetMessageId = emailData?.internetMessageId;

      if (!shipmentId || !internetMessageId || !clickedFTLShipment) return;
      try {
        await loadApp.saveFTLPricing({
          shipmentId,
          selectedMode,
          internetMessageId,
          formData: formDataRef.current,
          existingShipment: { ...clickedFTLShipment, quotedRates },
        });
        const refreshedEmailData = await loadApp.getEmails(internetMessageId);

        if (refreshedEmailData) {
          setEmailData(refreshedEmailData);
          const updatedShipment = emailShipmentsFtl(refreshedEmailData).find(
            (s) => s.shipmentId === shipmentId
          );

          if (updatedShipment) {
            setClickedFTLShipment(updatedShipment);
            const qr = updatedShipment.quotedRates;

            if (Array.isArray(qr) && qr.length > 0) {
              setOfferData(qr as QuotedRate[]);
            }
          }
        }
      } catch (err) {
        setOverallError(err instanceof Error ? err.message : UNEXPECTED_ERROR_MESSAGE);
      }
    },
    [
      clickedFTLShipment,
      emailData?.internetMessageId,
      loadApp,
      selectedMode,
      setClickedFTLShipment,
      setEmailData,
      setOfferData,
      setOverallError,
    ]
  );
  const handleCustomerOfferCommit = useCallback(
    (patch: CustomerOfferPatch) => {
      const id = primaryTmsOffer?.id;

      if (!id) return;
      setOfferData((prev) => {
        if (!prev) return prev;
        const next = prev.map((o) =>
          o.id === id
            ? {
                ...o,
                appliedCost: patch.appliedCost,
                appliedMargin: patch.appliedMargin,
                appliedMarkupPercent: patch.appliedMarkupPercent,
                totalBilledRate: patch.totalBilledRate,
                totalRate: patch.totalBilledRate,
                billed: patch.totalBilledRate,
                price: patch.totalBilledRate,
              }
            : o
        );

        void saveFtlPricingWithRates(next);
        return next;
      });
      setOfferMargins((m) => ({ ...m, [id]: patch.appliedMarkupPercent }));
    },
    [primaryTmsOffer?.id, saveFtlPricingWithRates]
  );
  const navBack = () => {
    setSelectedMode(null);
    setActiveComponent("Shipments");
  };
  const selectEquipment = (newEquipment: string) => {
    const nextFormData = { ...formDataRef.current, equipment: newEquipment };

    formDataRef.current = nextFormData;
    setFormData(nextFormData);
    void persistShipmentDetails(nextFormData);
  };
  const clearForm = () => {
    try {
      setFormData(DEFAULT_FTL_FORM_DATA);
      formDataRef.current = DEFAULT_FTL_FORM_DATA;
      setInferredFormFields({});
      setErrors({});
    } catch {
      setOverallError(UNEXPECTED_ERROR_MESSAGE);
    }
  };
  const resetToOriginal = async () => {
    setShowResetConfirm(false);
    const shipmentId = clickedFTLShipment?.shipmentId;
    const internetMessageId = emailData?.internetMessageId;

    if (!shipmentId || !internetMessageId) {
      setOverallError("Cannot reset: missing shipment or email.");
      return;
    }
    try {
      const key = `${FTL_ORIGINAL_STORAGE_KEY_PREFIX}${shipmentId}`;
      const stored = window.localStorage.getItem(key);

      if (!stored) {
        setOverallError("No original state found for this shipment.");
        return;
      }
      const requiredFields = JSON.parse(stored) as Record<string, unknown>;

      await loadApp.resetFTLRequiredFields({
        shipmentId,
        internetMessageId,
        requiredFields,
      });
      const refreshed = await loadApp.getEmails(internetMessageId);

      if (refreshed) setEmailData(refreshed);
      const refreshedShipment = refreshed
        ? emailShipmentsFtl(refreshed).find((s) => s.shipmentId === shipmentId)
        : undefined;
      const nd = refreshedShipment?.normalizedDraft;

      if (nd) {
        const form = nd.form;
        const equipmentCheck = (providerConfig.ftlEquipmentOptions ?? []).find(
          (option: string) => option.toUpperCase() === (form.equipment || "").toUpperCase()
        );
        const equipment = equipmentCheck ?? (form.equipment?.trim() ? form.equipment : "VAN");
        const nextFormData = { ...DEFAULT_FTL_FORM_DATA, ...form, equipment };

        formDataRef.current = nextFormData;
        setFormData(nextFormData);
        setDetailsSectionTab("shipment");
        lastSavedFormRef.current = serializeFtlFormData(nextFormData);
        setInferredFormFields(inferFieldsForFtlLoad(nd as NormalizedFtlShipmentDraft));
        setErrors({});
        const list = (refreshedShipment?.quotedRates ?? []) as QuotedRate[];

        setOfferData(list.length > 0 ? list : null);
        setOfferMargins(Object.fromEntries(list.map((offer) => [offer.id, offer.marginPct ?? 0])));
        setSelectedOffer(null);
      }
    } catch (err) {
      setOverallError(err instanceof Error ? err.message : UNEXPECTED_ERROR_MESSAGE);
    }
  };
  const submitPricing = async () => {
    const result = await submitPricingHook(setSelectedOffer);

    if (result.errors && Object.keys(result.errors).length > 0) {
      setErrors(result.errors);
    }
  };
  const handleNextClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedOffer) return;
    void selectOffer();
  };
  const selectOffer = async () => {
    try {
      const shipmentId = clickedFTLShipment?.shipmentId;
      const internetMessageId = emailData?.internetMessageId;

      if (!shipmentId || !internetMessageId) return;
      if (!selectedOffer) return;
      const list = offerData ?? [];
      const margins = Object.fromEntries(list.map((o) => [o.id, Number(offerMargins[o.id] ?? 0)]));

      await loadApp.saveFTLSelectedOffer({
        offerIds: [selectedOffer],
        margins,
        shipmentId,
        internetMessageId,
        ...statusHistoryIdentityPayload(emailData),
      });
      const refreshedEmailData = await loadApp.getEmails(internetMessageId);

      setEmailData(refreshedEmailData);
      navBack();
    } catch {
      setOverallError(UNEXPECTED_ERROR_MESSAGE);
    }
  };
  const handleChangeOfferMargin = React.useCallback((offerId: string, marginPct: number) => {
    setOfferMargins((prev) => ({
      ...prev,
      [offerId]: Math.round(marginPct * 100) / 100,
    }));
  }, []);
  const latestThumb = getLatestFeedbackValue(emailData);
  const feedbackIconName =
    typeof latestThumb === "boolean" ? (latestThumb ? "Like" : "Dislike") : undefined;
  const feedbackButtonStyles =
    typeof latestThumb === "boolean"
      ? {
          root: {
            border: `1px solid ${latestThumb ? "#107C10" : "#A4262C"}`,
            color: latestThumb ? "#107C10" : "#A4262C",
          },
          rootHovered: {
            border: `1px solid ${latestThumb ? "#0B6A0B" : "#7F161D"}`,
            color: latestThumb ? "#0B6A0B" : "#7F161D",
          },
          icon: {
            color: latestThumb ? "#107C10" : "#A4262C",
          },
        }
      : styles.secondaryButton;
  const handleSubmitFeedback = async (feedback: boolean, message: string) => {
    if (!emailData?.internetMessageId) return;
    setFeedbackSubmitting(true);
    try {
      const { feedback_entries } = await loadApp.saveEmailFeedback({
        internetMessageId: emailData.internetMessageId,
        feedback,
        feedbackContext: message,
      });

      setEmailData({
        ...emailData,
        feedback_entries,
      });
      setFeedbackDialogOpen(false);
    } catch (err) {
      setOverallError(err instanceof Error ? err.message : UNEXPECTED_ERROR_MESSAGE);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <Box className="ftl-container" sx={styles.container}>
      <FtlErrorMessageBar
        message={overallError}
        onDismiss={() => setOverallError(null)}
        errorRef={errorRef}
      />

      <FtlPanelHeader
        headerRef={headerRef}
        onBack={navBack}
        onFeedbackClick={() => setFeedbackDialogOpen(true)}
        feedbackDisabled={!emailData?.internetMessageId}
        feedbackIconName={feedbackIconName}
        feedbackButtonStyles={feedbackButtonStyles}
      />

      <Box sx={styles.requiredNote}>
        <Text styles={styles.requiredText}>* Required fields</Text>
      </Box>

      <form autoComplete="off">
        <Box sx={styles.section}>
          <FtlShipmentDetailsHeading
            bolCaps={providerConfig.ftlBolFormCapabilities}
            detailsSectionTab={detailsSectionTab}
            onShipmentTab={() => setDetailsSectionTab("shipment")}
            onBolTab={() => {
              setDetailsSectionTab("bol");
              applyFtlBolValidation();
            }}
          />
          {(!providerConfig.ftlBolFormCapabilities || detailsSectionTab === "shipment") && (
            <FtlShipmentDetailsGrid
              formData={formData}
              errors={errors}
              weightWarning={weightWarning}
              equipmentOptions={providerConfig.ftlEquipmentOptions ?? []}
              onInputChange={handleInputChange}
              onPersistBlur={() => void persistShipmentDetails()}
              getTextFieldStyles={getTextFieldStyles}
              onSelectEquipment={selectEquipment}
            />
          )}
          {detailsSectionTab === "bol" && providerConfig.ftlBolFormCapabilities && (
            <BolDetailsForm
              form={pickBolFormSlice(formData)}
              errors={errors}
              inferredFormFields={inferredFormFields}
              caps={providerConfig.ftlBolFormCapabilities}
              onFieldChange={handleBolFieldChange}
              onFieldBlur={() => {
                void persistShipmentDetails();
                applyFtlBolValidation();
              }}
              getTextFieldStyles={getTextFieldStyles}
            />
          )}
        </Box>

        <FtlPricingActions
          onViewOffers={submitPricing}
          onNext={handleNextClick}
          onReset={() => setShowResetConfirm(true)}
          onClear={clearForm}
          isFormValid={isFormValid}
          hasSelectedOffer={selectedOffer !== null}
          quotedAt={clickedFTLShipment?.quotedAt}
          hasExistingRates={(clickedFTLShipment?.quotedRates?.length ?? 0) > 0}
        />
      </form>
      <ConfirmDialog
        open={showResetConfirm}
        title="Reset shipment?"
        message="This will restore the shipment to its original state and save it to the server."
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => void resetToOriginal()}
        onCancel={() => setShowResetConfirm(false)}
      />
      {loading && (
        <Box className="offers-loading-container" ref={loaderRef} sx={styles.loadingWrapper}>
          <Spinner size={SpinnerSize.large} label={offersLoadingLabel} />
        </Box>
      )}
      {!loading && (
        <>
          <FtlTmsOffersBlock
            customerOfferUiKey={customerOfferUiKey}
            primaryTmsOffer={primaryTmsOffer}
            hasAnyTmsOffers={tmsRowOffers.length > 0}
            onCustomerOfferCommit={handleCustomerOfferCommit}
            tmsSectionTitle={tmsSectionTitle}
            tmsBranding={tmsBranding}
            tmsRowOffers={tmsRowOffers}
            isSingleTmsOffer={isSingleTmsOffer}
            selectedOffer={selectedOffer}
            onSelectOffer={setSelectedOffer}
            offerMargins={offerMargins}
            onMarginChange={handleChangeOfferMargin}
            showConfidence={!!providerConfig.showConfidence}
            confidenceField={providerConfig.confidenceField ?? "confidenceLevel"}
          />
          <RatingEnginesSection
            marketRates={clickedFTLShipment?.marketRates}
            ratingEngineProviders={emailData?.providerSetup?.ratingEngines}
          />
        </>
      )}
      <FeedbackDialog
        open={feedbackDialogOpen}
        initialFeedback={null}
        initialMessage=""
        submitting={feedbackSubmitting}
        onSubmit={(fb, msg) => void handleSubmitFeedback(fb, msg)}
        onCancel={() => {
          if (!feedbackSubmitting) setFeedbackDialogOpen(false);
        }}
      />
    </Box>
  );
}
